import { isChatMemoryPath, isChatPath } from './chat-route'

const RETURN_PATH_KEY = 'ontology.chat.return-path'
const MEMORY_RETURN_PATH_KEY = 'ontology.chat.memory-return-path'
const EXPAND_RAIL_KEY = 'ontology.chat.expand-rail'

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && 'sessionStorage' in window
}

/**
 * Remember the last non-chat location so "Dock to side" can return there.
 * Chat paths are ignored so navigating between `/chat` and `/chat/:id`
 * does not overwrite the page the user came from.
 */
export function rememberChatReturnPath(pathname: string, search = ''): void {
  if (!hasSessionStorage()) return
  if (isChatPath(pathname)) return
  const value = `${pathname}${search}`
  if (value.length === 0) return
  window.sessionStorage.setItem(RETURN_PATH_KEY, value)
}

/**
 * Remember the chat surface that opened shared memory so Back can restore
 * either the landing (`/chat`) or the thread (`/chat/:id`). Non-chat
 * entry points (Settings, the rail) should pass the destination they want.
 */
export function rememberMemoryReturnPath(pathname: string): void {
  if (!hasSessionStorage()) return
  if (isChatMemoryPath(pathname)) return
  if (!isChatPath(pathname)) return
  window.sessionStorage.setItem(MEMORY_RETURN_PATH_KEY, pathname)
}

/** Read and clear the path memory's Back control should restore. */
export function consumeMemoryReturnPath(): string | null {
  if (!hasSessionStorage()) return null
  const value = window.sessionStorage.getItem(MEMORY_RETURN_PATH_KEY)
  if (value === null || value.length === 0) return null
  window.sessionStorage.removeItem(MEMORY_RETURN_PATH_KEY)
  return value
}

/** Read and clear the path "Dock to side" should restore. */
export function consumeChatReturnPath(): string | null {
  if (!hasSessionStorage()) return null
  const value = window.sessionStorage.getItem(RETURN_PATH_KEY)
  if (value === null || value.length === 0) return null
  window.sessionStorage.removeItem(RETURN_PATH_KEY)
  return value
}

/**
 * One-shot signal: the next `SidebarShell` mount should start expanded,
 * even below `xl` where the default is collapsed. "Dock to side" unmounts
 * the rail on `/chat`, so without this the remount would land on the 48 px
 * strip.
 *
 * The flag is peeked for the initial expanded state and cleared after
 * mount — consuming in the initializer would lose the signal under
 * React Strict Mode's double-invoke.
 */
export function requestRailExpandOnNextMount(): void {
  if (!hasSessionStorage()) return
  window.sessionStorage.setItem(EXPAND_RAIL_KEY, '1')
}

export function peekRailExpandOnNextMount(): boolean {
  if (!hasSessionStorage()) return false
  return window.sessionStorage.getItem(EXPAND_RAIL_KEY) === '1'
}

export function consumeRailExpandOnNextMount(): void {
  if (!hasSessionStorage()) return
  window.sessionStorage.removeItem(EXPAND_RAIL_KEY)
}
